import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';

export const AuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'timeout'>('loading');
  const sentRef = useRef(false);
  const statusRef = useRef<'loading' | 'success' | 'timeout'>('loading');

  useEffect(() => {
    // Reset esplicito ad ogni mount
    sentRef.current = false;
    statusRef.current = 'loading';

    // 1. Estrazione DIRETTA dall'URL hash o query (più sicura contro cache/storage partizionato)
    let urlProviderToken: string | null = null;
    let urlProviderRefreshToken: string | null = null;
    let urlAccessToken: string | null = null;
    let urlRefreshToken: string | null = null;

    const hash = window.location.hash;
    const search = window.location.search;

    if (hash && hash.includes('access_token')) {
      const params = new URLSearchParams(hash.substring(1));
      urlAccessToken = params.get('access_token');
      urlRefreshToken = params.get('refresh_token');
      urlProviderToken = params.get('provider_token');
      urlProviderRefreshToken = params.get('provider_refresh_token');
    } else if (search && search.includes('access_token')) {
      const params = new URLSearchParams(search);
      urlAccessToken = params.get('access_token');
      urlRefreshToken = params.get('refresh_token');
      urlProviderToken = params.get('provider_token');
      urlProviderRefreshToken = params.get('provider_refresh_token');
    }

    // Fallback ai backup temporanei di index.html se non trovati nell'URL (perché già consumati dal client Supabase)
    if (!urlAccessToken) urlAccessToken = localStorage.getItem('temp_supabase_access_token') || sessionStorage.getItem('temp_supabase_access_token');
    if (!urlRefreshToken) urlRefreshToken = localStorage.getItem('temp_supabase_refresh_token') || sessionStorage.getItem('temp_supabase_refresh_token');
    if (!urlProviderToken) urlProviderToken = localStorage.getItem('temp_google_access_token') || sessionStorage.getItem('temp_google_access_token');
    if (!urlProviderRefreshToken) urlProviderRefreshToken = localStorage.getItem('temp_google_refresh_token') || sessionStorage.getItem('temp_google_refresh_token');

    function sendTokens(sessionOrTokens: any) {
      if (sentRef.current) return;

      sentRef.current = true;
      statusRef.current = 'success';
      setStatus('success');

      // Uniamo i dati della sessione con quelli estratti direttamente dall'URL o dai backup
      const finalAccessToken = sessionOrTokens.access_token || urlAccessToken;
      const finalRefreshToken = sessionOrTokens.refresh_token || urlRefreshToken;
      const finalProviderToken = sessionOrTokens.provider_token || sessionOrTokens.provider_access_token || urlProviderToken;
      const finalProviderRefreshToken = sessionOrTokens.provider_refresh_token || urlProviderRefreshToken;

      if (finalProviderToken) {
        try {
          localStorage.setItem('google_access_token', finalProviderToken);
          sessionStorage.setItem('google_access_token', finalProviderToken);
          
          localStorage.setItem('google_workspace_authorized', 'true');
          sessionStorage.setItem('google_workspace_authorized', 'true');
          localStorage.removeItem('google_workspace_authorizing_in_progress');
          sessionStorage.removeItem('google_workspace_authorizing_in_progress');
        } catch (e) {
          console.error('AuthCallback: Error writing directly to storage:', e);
        }
      }
      if (finalProviderRefreshToken) {
        try {
          localStorage.setItem('google_refresh_token', finalProviderRefreshToken);
          sessionStorage.setItem('google_refresh_token', finalProviderRefreshToken);
        } catch (e) {}
      }

      // Pulizia dei backup temporanei per sicurezza ed igiene del browser
      try {
        localStorage.removeItem('temp_google_access_token');
        sessionStorage.removeItem('temp_google_access_token');
        localStorage.removeItem('temp_google_refresh_token');
        sessionStorage.removeItem('temp_google_refresh_token');
        localStorage.removeItem('temp_supabase_access_token');
        sessionStorage.removeItem('temp_supabase_access_token');
        localStorage.removeItem('temp_supabase_refresh_token');
        sessionStorage.removeItem('temp_supabase_refresh_token');
      } catch (e) {}

      const messageData = {
        type: 'OAUTH_AUTH_SUCCESS',
        session: {
          access_token: finalAccessToken,
          refresh_token: finalRefreshToken,
          provider_token: finalProviderToken,
          provider_refresh_token: finalProviderRefreshToken,
        },
        timestamp: Date.now(),
      };

      if (window.opener) {
        try {
          window.opener.postMessage(messageData, '*');
        } catch (e) {
          console.error('AuthCallback: Errore postMessage:', e);
        }
      }

      try {
        const channel = new BroadcastChannel('supabase_auth');
        channel.postMessage(messageData);
      } catch (e) {}

      try {
        localStorage.setItem('supabase_auth_success', JSON.stringify(messageData));
      } catch (e) {}

      setTimeout(() => window.close(), 1000);
      
      setTimeout(() => {
        if (!window.closed) {
          setStatus('success'); // Assicura che la UI mostri "completato"
        }
      }, 2000);
    }

    const checkUrl = () => {
      if (urlAccessToken && urlRefreshToken) {
        sendTokens({ 
          access_token: urlAccessToken, 
          refresh_token: urlRefreshToken, 
          provider_token: urlProviderToken, 
          provider_refresh_token: urlProviderRefreshToken 
        });
        return true; 
      }
      return false;
    };

    if (checkUrl()) return;

    // 2. Fallback: usiamo il client Supabase con controllo periodico
    let attempts = 0;
    const sessionInterval = setInterval(async () => {
      attempts++;
      const { data: { session }, error } = await supabase.auth.getSession();
      if (session && !sentRef.current) {
        clearInterval(sessionInterval);
        sendTokens(session);
      }
      if (attempts > 10) clearInterval(sessionInterval);
    }, 1000);

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN' || event === 'INITIAL_SESSION') {
        if (session && !sentRef.current) {
          sendTokens(session);
        }
      }
    });

    const timeout = setTimeout(() => {
      // ✅ Usa la ref, non la variabile di stato (evita closure stale)
      if (statusRef.current === 'loading') {
        console.error('AuthCallback: Timeout');
        statusRef.current = 'timeout';
        setStatus('timeout');
      }
    }, 15000);

    return () => {
      subscription.unsubscribe();
      clearTimeout(timeout);
    };
  }, []); // ✅ Array vuoto: dipendenze corrette, nessun re-run spurio

  return (
    <div className="min-h-screen bg-zinc-50 flex flex-col items-center justify-center p-4">
      <div className="bg-white p-8 rounded-2xl shadow-xl max-w-md w-full text-center">
        {status === 'loading' && (
          <>
            <div className="w-16 h-16 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Autenticazione in corso...</h2>
            <p className="text-zinc-500">Attendi mentre completiamo l'accesso.</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Accesso completato!</h2>
            <p className="text-zinc-500">Puoi chiudere questa finestra.</p>
          </>
        )}
        {status === 'timeout' && (
          <>
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <svg className="w-8 h-8 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-zinc-900 mb-2">Errore di accesso</h2>
            <p className="text-zinc-500 mb-6">La procedura ha impiegato troppo tempo.</p>
            <button 
              onClick={() => window.close()}
              className="px-6 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800"
            >
              Chiudi finestra
            </button>
          </>
        )}
      </div>
    </div>
  );
};